import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, ArrowRight, CircleHelp, FileUp, LayoutTemplate, PencilRuler, Sparkles } from 'lucide-react';
import '../../assets/styles/Survey.css';
import '../../assets/styles/ChooseSurvey.css';

import Templates from './TemplatesPulse/Templates';
import CreateSurvey from './CreateSurvey/CreateSurvey';
import ProcessSurvey from './CreateSurvey/ProcessSurvey';
import PromptSurvey from './CreateSurvey/PromptSurvey.jsx';
import HelpModal from './HelpModal';
import SectionEyebrow from '../common/SectionEyebrow';
import { TemplateProvider } from '../../assets/js/TemplateContext';
import { useLanguage } from '../../assets/js/LanguageContext';

const options = [
  { key: 'template', icon: LayoutTemplate, tone: '#7C3AED', labelKey: 'choose_template', descKey: 'choose_template_desc', tagKey: 'choose_template_tag' },
  { key: 'diagnostic', icon: PencilRuler, tone: '#2563EB', labelKey: 'choose_diagnostic', descKey: 'choose_diagnostic_desc' },
  { key: 'process', icon: FileUp, tone: '#0D9488', labelKey: 'choose_process', descKey: 'choose_process_desc' },
  { key: 'prompt', icon: Sparkles, tone: '#C026D3', labelKey: 'choose_prompt', descKey: 'choose_prompt_desc', tagKey: 'choose_prompt_tag' }
];

const SurveySelector = () => {
  const { t } = useLanguage();
  const [selectedOption, setSelectedOption] = useState('');
  const [showHelpModal, setShowHelpModal] = useState(false);

  useEffect(() => {
    const hasSeenHelp = localStorage.getItem('helpShown');
    if (!hasSeenHelp) {
      const timer = setTimeout(() => {
        setShowHelpModal(true);
      }, 1000);

      return () => clearTimeout(timer);
    }

    return undefined;
  }, []);

  const resetSelection = () => {
    setSelectedOption('');
  };

  const handleCloseHelp = () => {
    setShowHelpModal(false);
    localStorage.setItem('helpShown', 'true');
  };

  const helpModal = <HelpModal isOpen={showHelpModal} onClose={handleCloseHelp} showOnFirstVisit />;

  if (selectedOption === '') {
    return (
      <div className="tp-choose">
        <Link to="/dashboard" className="tp-choose__back">
          <ArrowLeft size={16} strokeWidth={2} aria-hidden="true" />
          {t('sd_back')}
        </Link>

        <header className="tp-choose__head">
          <div className="tp-choose__heading">
            <SectionEyebrow>{t('choose_page_eyebrow')}</SectionEyebrow>
            <h1 className="tp-choose__title">{t('choose_page_title')}</h1>
            <p className="tp-choose__lead">{t('choose_page_lead')}</p>
          </div>
          <button type="button" className="btn btn--outline btn--sm tp-choose__help" onClick={() => setShowHelpModal(true)}>
            <CircleHelp size={16} strokeWidth={2} aria-hidden="true" />
            {t('choose_help')}
          </button>
        </header>

        <ul className="tp-choose__grid">
          {options.map(({ key, icon: Icon, tone, labelKey, descKey, tagKey }) => (
            <li key={key}>
              <button
                type="button"
                className="tp-choose__card"
                style={{ '--choose-tone': tone }}
                onClick={() => setSelectedOption(key)}
              >
                <span className="tp-choose__icon" aria-hidden="true">
                  <Icon size={24} strokeWidth={1.75} />
                </span>
                <span className="tp-choose__body">
                  <span className="tp-choose__card-title">
                    {t(labelKey)}
                    {tagKey && <span className="tp-choose__tag">{t(tagKey)}</span>}
                  </span>
                  <span className="tp-choose__desc">{t(descKey)}</span>
                </span>
                <span className="tp-choose__go">
                  {t('choose_start')}
                  <ArrowRight size={16} strokeWidth={2} aria-hidden="true" />
                </span>
              </button>
            </li>
          ))}
        </ul>

        {helpModal}
      </div>
    );
  }

  if (selectedOption === 'template') {
    return (
      <TemplateProvider>
        <Templates onBack={resetSelection} />
        {helpModal}
      </TemplateProvider>
    );
  }

  if (selectedOption === 'diagnostic') {
    return (
      <>
        <CreateSurvey onBack={resetSelection} />
        {helpModal}
      </>
    );
  }

  if (selectedOption === 'prompt') {
    return (
      <>
        <PromptSurvey onBack={resetSelection} />
        {helpModal}
      </>
    );
  }

  return (
    <>
      <ProcessSurvey onBack={resetSelection} />
      {helpModal}
    </>
  );
};

export default SurveySelector;
